import { queryOptions } from "@tanstack/react-query";
import { createQueryGroup } from "../../core/api/queries";
import {
  AiWeeklyTemplateSuggestionId,
  AuthorizationId,
  PatientId,
} from "../../shared/schema/schema";

export default createQueryGroup(({ api }) => ({
  /**
   * The authorizations the builder can work from, plus this patient's remaining
   * generation quota. Both come back together so the modal can disable
   * "Generate" before the coordinator has picked anything.
   */
  authorizations: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["aiWeeklyTemplateBuilder", patientId, "authorizations"],
      queryFn: () =>
        api.get("./patients/:patientId/ai_weekly_template_builder/authorizations", {
          path: { patientId },
        }),
    }),

  /**
   * The patient's current weekly template shifts. Used only to decide whether to
   * show the "no template yet" prompt — the AngularJS section below this one
   * renders the template itself.
   */
  existingTemplate: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["aiWeeklyTemplateBuilder", patientId, "existingTemplate"],
      queryFn: () =>
        api.get("./patients/:patientId/weekly_template_shifts", {
          path: { patientId },
        }),
      select: ({ shifts }) => shifts,
    }),

  /**
   * The authorization's periods and pre-computed calendar facts. These are the
   * same numbers the server hands the model and verifies its answer against, so
   * what the coordinator reads here is what gets enforced.
   */
  context: (authorizationId: AuthorizationId) =>
    queryOptions({
      queryKey: ["aiWeeklyTemplateBuilder", authorizationId, "context"],
      queryFn: () =>
        api.get("./authorizations/:authorizationId/ai_weekly_template_builder/context", {
          path: { authorizationId },
        }),
    }),

  /**
   * One stored suggestion, returned exactly as it was recorded — never
   * recomputed, so it shows what the coordinator saw at the time even if the
   * authorization has changed since.
   */
  suggestionDetail: (
    authorizationId: AuthorizationId,
    suggestionId: AiWeeklyTemplateSuggestionId
  ) =>
    queryOptions({
      queryKey: ["aiWeeklyTemplateBuilder", authorizationId, "suggestion", suggestionId],
      queryFn: () =>
        api.get(
          "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId",
          { path: { authorizationId, suggestionId } }
        ),
      // Immutable once written, so there is nothing to refetch.
      staleTime: Infinity,
    }),
}));
